import { Controller } from '@hotwired/stimulus';
import Dropzone from 'dropzone';
import { Modal } from 'bootstrap';

Dropzone.autoDiscover = false;

export default class extends Controller {

    static targets = [
        'droparea',
        'modal',
        'modalBody',
        'modalTitle'
    ];

    static values = {
        url: String,
        deleteUrl: String,
    };

    declare readonly urlValue: string;

    declare readonly deleteUrlValue: string;

    declare readonly dropareaTarget: HTMLElement;

    declare readonly modalTarget: HTMLElement;

    declare readonly modalBodyTarget: HTMLElement;

    declare readonly modalTitleTarget: HTMLElement;

    dropzone!: Dropzone;

    modal!: Modal;

    connect(): void {

        this.modal = new Modal(
            this.modalTarget
        );

        const options: any = {

            url: this.urlValue,

            paramName: 'file',

            maxFilesize: 20,

            acceptedFiles: '.pdf,.jpg,.jpeg,.png,.doc,.docx',

            addRemoveLinks: true,

            headers: {
                'X-Requested-With': 'XMLHttpRequest',
            },

            success: (
                file: any,
                response: any
            ): void => {

                if (!response.success) {
                    return;
                }

                this.appendDocumentCard(
                    response.document
                );

                // limpiar preview interno dropzone
                this.dropzone.removeFile(file);
            },
        };

        this.dropzone = new Dropzone(
            this.dropareaTarget,
            options
        );
    }

    openPreview(
        event: Event
    ): void {

        event.preventDefault();

        const button = event.currentTarget as HTMLElement;

        const url = button.dataset.previewUrl;

        const type = button.dataset.previewType;

        const title = button.dataset.previewTitle || 'Vista previa';

        if (!url) {
            return;
        }

        this.modalTitleTarget.textContent = title;

        if (type === 'image') {
            this.modalBodyTarget.innerHTML = `
                <img
                    src="${this.escapeHtml(url)}"
                    alt="${this.escapeHtml(title)}"
                    class="img-fluid rounded"
                    style="max-height:75vh;object-fit:contain;"
                >
            `;
        } else if (type === 'pdf') {
            this.modalBodyTarget.innerHTML = `
                <iframe
                    src="${this.escapeHtml(url)}"
                    title="${this.escapeHtml(title)}"
                    class="w-100 border rounded bg-white"
                    style="height:75vh;"
                ></iframe>
            `;
        } else {
            this.modalBodyTarget.innerHTML = `
                <div class="py-5">
                    <a href="${this.escapeHtml(url)}" target="_blank" class="btn btn-primary">
                        Abrir documento
                    </a>
                </div>
            `;
        }

        this.modal.show();
    }

    async deleteDocument(
        event: Event
    ): Promise<void> {

        event.preventDefault();

        const button = event.currentTarget as HTMLElement;

        const id = button.dataset.id;

        if (!id) {
            return;
        }

        const response = await fetch(
            this.deleteUrlValue.replace(
                '/0',
                '/' + id
            ),
            {
                method: 'DELETE',

                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                },
            }
        );

        const json = await response.json();

        if (!json.success) {
            return;
        }

        const card = button.closest(
            '.document-card'
        );

        if (card) {
            card.remove();
        }
    }

    appendDocumentCard(
        doc: any
    ): void {

        const container = window.document.getElementById(
            'documents-grid'
        );

        if (!container) {
            return;
        }

        const preview = doc.previewUrl || '';

        const fileUrl = doc.fileUrl || `/uploads/documents/registers/${doc.file}`;

        const previewUrl = doc.isPdf
            ? fileUrl
            : preview;

        const previewType = doc.isImage
            ? 'image'
            : (doc.isPdf ? 'pdf' : 'file');

        const html = `
            <div class="col-md-4 mb-3 document-card">

                <div class="card h-100">

                    <div class="card-body text-center">

                        ${preview
                ?
                `<button
                                type="button"
                                class="btn p-0 border-0 bg-transparent w-100"
                                data-action="dropzone#openPreview"
                                data-preview-url="${this.escapeHtml(previewUrl)}"
                                data-preview-type="${previewType}"
                                data-preview-title="${this.escapeHtml(doc.name)}"
                            >
                                <img
                                src="${this.escapeHtml(preview)}"
                                class="img-fluid mb-3 rounded border"
                                style="height:250px;object-fit:contain;"
                            >
                            </button>`
                :
                `<div class="py-5 text-muted">
                                ${doc.isPdf
                                    ? `<button
                                            type="button"
                                            class="btn p-0 border-0 bg-transparent text-muted w-100"
                                            data-action="dropzone#openPreview"
                                            data-preview-url="${this.escapeHtml(fileUrl)}"
                                            data-preview-type="pdf"
                                            data-preview-title="${this.escapeHtml(doc.name)}"
                                        >
                                            <i class="bi bi-file-earmark-pdf fs-1 d-block mb-2"></i>
                                            Ver PDF
                                        </button>`
                                    : 'Sin preview'
                                }
                            </div>`
            }

                        <div class="fw-bold small mb-3">
                            ${this.escapeHtml(doc.name)}
                        </div>

                        <div class="d-flex justify-content-between">

                            <button
                                type="button"
                                class="btn btn-danger btn-sm"
                                data-id="${doc.id}"
                                data-action="dropzone#deleteDocument"
                            >
                                Eliminar
                            </button>

                            <a
                                href="${this.escapeHtml(fileUrl)}"
                                class="btn btn-success btn-sm"
                                target="_blank"
                            >
                                Descargar
                            </a>

                        </div>

                    </div>

                </div>

            </div>
        `;

        container.insertAdjacentHTML(
            'beforeend',
            html
        );
    }

    private escapeHtml(
        value: string
    ): string {

        return value
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');
    }
}
